import type {
  PoseLandmarks,
  CalibrationResult,
  Gender,
  WornClothingType,
  MeasurementValue,
  MeasurementMethod,
  ConfidenceLevel,
  BodyMeasurements,
} from '@/types/measurements';
import { CLOTHING_COMPENSATION } from '@/types/measurements';
import type { SilhouetteObservation } from './cross-section';
import { computeCircumferenceFromObservations } from './cross-section';

// ─── MediaPipe Pose Landmark Indices ───────────────────────
const LEFT_SHOULDER = 11;
const RIGHT_SHOULDER = 12;
const LEFT_ELBOW = 13;
const RIGHT_ELBOW = 14;
const LEFT_WRIST = 15;
const RIGHT_WRIST = 16;
const LEFT_HIP = 23;
const RIGHT_HIP = 24;

// ─── Depth-to-Width Ratios ─────────────────────────────────
// Used to estimate body depth from front silhouette width
// for elliptical circumference calculation.
// Human torsos are roughly 2.5-3x wider than deep.
const DEPTH_RATIOS: Record<
  'male' | 'female',
  { chest: number; waist: number; hip: number }
> = {
  male: { chest: 0.38, waist: 0.42, hip: 0.35 },
  female: { chest: 0.35, waist: 0.40, hip: 0.38 },
};

// ─── Hybrid Blending Weights ─────────────────────────────────
// Front-only: lean heavily on proportional estimates since
// silhouette width can include arms and clothing.
// With side photo: trust the silhouette-based measurement more.
const BLEND_WEIGHTS = {
  frontOnly: { silhouette: 0.30, proportional: 0.70 },
  withSide: { silhouette: 0.80, proportional: 0.20 },
};

// ─── Circumference Sanity Bounds ─────────────────────────────
// Anatomically plausible ranges to catch bad silhouette data.
const CIRCUMFERENCE_BOUNDS: Record<
  string,
  { min: number; maxRatio: number }
> = {
  chest: { min: 60, maxRatio: 0.70 },
  waist: { min: 50, maxRatio: 0.60 },
  hip: { min: 65, maxRatio: 0.72 },
};

// ─── Types ─────────────────────────────────────────────────

export interface MeasurementInput {
  landmarks: PoseLandmarks;
  segmentationMask: ImageData | null;
  calibration: CalibrationResult;
  gender: Gender;
  wornClothing: WornClothingType;
  userHeightCm: number;
  imageWidth: number;
  imageHeight: number;
  // Optional side photo data for improved circumference accuracy
  sideLandmarks?: PoseLandmarks;
  sideSegmentationMask?: ImageData | null;
  sideImageWidth?: number;
  sideImageHeight?: number;
  // Optional rotation data for multi-view circumference (Cauchy's formula)
  rotationObservations?: {
    chest: SilhouetteObservation[];
    waist: SilhouetteObservation[];
    hip: SilhouetteObservation[];
  };
}

export interface MeasurementResult {
  measurements: Partial<BodyMeasurements>;
  overallConfidence: number;
  measuredCount: number;
}

// ─── Core Extraction ───────────────────────────────────────

/**
 * Extract core 7 body measurements from pose landmarks and optional segmentation mask.
 *
 * Phase 1 Gold tier measurements:
 *  1. Height (user-provided)
 *  2. Shoulder width (landmark distance)
 *  3. Chest circumference (silhouette + ellipse)
 *  4. Waist circumference (silhouette + ellipse)
 *  5. Hip circumference (silhouette + ellipse)
 *  6. Arm length (multi-segment landmarks)
 *  7. Back length (landmark vertical distance)
 */
export function extractMeasurements(
  input: MeasurementInput
): MeasurementResult {
  const {
    landmarks,
    segmentationMask,
    calibration,
    gender,
    wornClothing,
    userHeightCm,
    imageWidth,
    imageHeight,
  } = input;

  const pxPerCm = calibration.pixelsPerCm;
  const measurements: Partial<BodyMeasurements> = {};
  const confidences: number[] = [];

  // 1. Height — user-provided, high confidence
  measurements.height = createMeasurement(
    userHeightCm,
    0.95,
    'direct_landmark'
  );
  confidences.push(0.95);

  // 2. Shoulder width — distance between left and right shoulder landmarks
  const lShoulder = landmarks[LEFT_SHOULDER]!;
  const rShoulder = landmarks[RIGHT_SHOULDER]!;
  const shoulderVisMean =
    (lShoulder.visibility + rShoulder.visibility) / 2;

  if (shoulderVisMean > 0.3) {
    const shoulderPx = landmarkDistPx(
      lShoulder,
      rShoulder,
      imageWidth,
      imageHeight
    );
    const shoulderCm = shoulderPx / pxPerCm;
    const confidence = calculateConfidence('direct_landmark', shoulderVisMean);
    measurements.shoulderWidth = createMeasurement(
      shoulderCm,
      confidence,
      'direct_landmark'
    );
    confidences.push(confidence);
  }

  // 3. Arm length — shoulder → elbow → wrist (better-visible side)
  const armResult = measureArmLength(
    landmarks,
    pxPerCm,
    imageWidth,
    imageHeight
  );
  if (armResult) {
    measurements.armLength = armResult;
    confidences.push(armResult.confidence);
  }

  // 4. Back length — vertical distance from neck midpoint to hip midpoint
  const backResult = measureBackLength(landmarks, pxPerCm, imageHeight);
  if (backResult) {
    measurements.backLength = backResult;
    confidences.push(backResult.confidence);
  }

  // 5-7. Circumferences — priority cascade:
  //   1. Rotation data (4+ keyframes) → Cauchy's formula
  //   2. Side photo → existing ellipse model with real depth
  //   3. Front only → ellipse with estimated depth
  //   4. No segmentation → pure proportional estimates

  const depthRatios =
    gender === 'female' ? DEPTH_RATIOS.female : DEPTH_RATIOS.male;

  const shoulderMidY = (lShoulder.y + rShoulder.y) / 2;
  const lHip = landmarks[LEFT_HIP]!;
  const rHip = landmarks[RIGHT_HIP]!;
  const hipMidY = (lHip.y + rHip.y) / 2;
  const torsoSpan = hipMidY - shoulderMidY;

  // Chest: 30% down from shoulders toward hips (fullest part)
  const chestY = shoulderMidY + torsoSpan * 0.3;
  // Waist: 60% down from shoulders toward hips (narrowest part)
  const waistY = shoulderMidY + torsoSpan * 0.6;
  // Hip: at hip landmark level
  const hipY = hipMidY;

  // Proportional estimates (always computed as fallback/blend target)
  const propChest = userHeightCm * (gender === 'female' ? 0.51 : 0.52);
  const propWaist = userHeightCm * (gender === 'female' ? 0.4 : 0.44);
  const propHip = userHeightCm * (gender === 'female' ? 0.55 : 0.53);

  // Body edge X limits for arm exclusion in silhouette scan
  const bodyLeftX = Math.min(lShoulder.x, lHip.x);
  const bodyRightX = Math.max(rShoulder.x, rHip.x);

  // Side photo only needs segmentation mask for depth measurement;
  // landmarks are optional (hard to detect from a side angle)
  const hasSidePhoto = !!input.sideSegmentationMask;
  const weights = hasSidePhoto ? BLEND_WEIGHTS.withSide : BLEND_WEIGHTS.frontOnly;

  // Check for rotation data first (highest accuracy path)
  let circumferencesFromRotation = false;
  if (input.rotationObservations) {
    const rotObs = input.rotationObservations;
    const entries: Array<{
      key: 'chestCircumference' | 'waistCircumference' | 'hipCircumference';
      observations: SilhouetteObservation[];
      propVal: number;
      boundsKey: string;
    }> = [
      { key: 'chestCircumference', observations: rotObs.chest, propVal: propChest, boundsKey: 'chest' },
      { key: 'waistCircumference', observations: rotObs.waist, propVal: propWaist, boundsKey: 'waist' },
      { key: 'hipCircumference', observations: rotObs.hip, propVal: propHip, boundsKey: 'hip' },
    ];

    for (const entry of entries) {
      if (entry.observations.length >= 2) {
        // Use Cauchy's formula with proportional blend (90% measured, 10% proportional)
        const cauchyCirc = computeCircumferenceFromObservations(entry.observations);
        const blended = cauchyCirc * 0.9 + entry.propVal * 0.1;

        // Sanity bounds check
        const bounds = CIRCUMFERENCE_BOUNDS[entry.boundsKey]!;
        const maxVal = userHeightCm * bounds.maxRatio;
        let finalValue = blended;
        let method: MeasurementMethod = 'multi_view_circumference';
        let confidence = multiViewConfidence(entry.observations.length);

        if (finalValue < bounds.min || finalValue > maxVal) {
          finalValue = entry.propVal;
          method = 'proportional_estimate';
          confidence = 0.55;
        }

        const compensated = applyClothingCompensation(
          finalValue,
          entry.boundsKey as 'chest' | 'waist' | 'hip',
          wornClothing
        );
        measurements[entry.key] = createMeasurement(compensated, confidence, method);
        confidences.push(confidence);
      }
    }

    // If all three were computed from rotation, skip the ellipse path
    circumferencesFromRotation = !!(
      measurements.chestCircumference &&
      measurements.waistCircumference &&
      measurements.hipCircumference
    );
  }

  if (!circumferencesFromRotation && segmentationMask) {
    // Measure side photo depth if available
    let sideDepths: { chest: number | null; waist: number | null; hip: number | null } | null = null;
    if (hasSidePhoto && input.sideSegmentationMask && input.sideImageWidth) {
      const sidePxPerCm = pxPerCm; // Same calibration (same person, same distance assumed)
      sideDepths = measureSideDepths(
        input.sideSegmentationMask,
        sidePxPerCm,
        chestY,
        waistY,
        hipY
      );
    }

    const circumferences = measureCircumferencesFromMask(
      segmentationMask,
      pxPerCm,
      depthRatios,
      chestY,
      waistY,
      hipY,
      bodyLeftX,
      bodyRightX,
      sideDepths
    );

    // Blend silhouette-based with proportional for each circumference
    const circumferenceEntries: Array<{
      key: 'chestCircumference' | 'waistCircumference' | 'hipCircumference';
      silhouetteVal: number | null;
      propVal: number;
      boundsKey: string;
    }> = [
      { key: 'chestCircumference', silhouetteVal: circumferences.chest, propVal: propChest, boundsKey: 'chest' },
      { key: 'waistCircumference', silhouetteVal: circumferences.waist, propVal: propWaist, boundsKey: 'waist' },
      { key: 'hipCircumference', silhouetteVal: circumferences.hip, propVal: propHip, boundsKey: 'hip' },
    ];

    for (const entry of circumferenceEntries) {
      let finalValue: number;
      let confidence: number;
      let method: MeasurementMethod;

      if (entry.silhouetteVal !== null) {
        // Hybrid blend
        finalValue = entry.silhouetteVal * weights.silhouette + entry.propVal * weights.proportional;
        confidence = hasSidePhoto ? 0.88 : 0.65;
        method = 'computed_circumference';
      } else {
        finalValue = entry.propVal;
        confidence = 0.55;
        method = 'proportional_estimate';
      }

      // Sanity check: clamp to anatomical bounds
      const bounds = CIRCUMFERENCE_BOUNDS[entry.boundsKey]!;
      const maxVal = userHeightCm * bounds.maxRatio;
      if (finalValue < bounds.min || finalValue > maxVal) {
        finalValue = entry.propVal;
        confidence = 0.55;
        method = 'proportional_estimate';
      }

      const compensated = applyClothingCompensation(
        finalValue,
        entry.boundsKey as 'chest' | 'waist' | 'hip',
        wornClothing
      );
      measurements[entry.key] = createMeasurement(compensated, confidence, method);
      confidences.push(confidence);
    }
  } else if (!circumferencesFromRotation) {
    // Pure proportional fallback when segmentation mask is unavailable
    measurements.chestCircumference = createMeasurement(
      applyClothingCompensation(propChest, 'chest', wornClothing),
      0.55,
      'proportional_estimate'
    );
    measurements.waistCircumference = createMeasurement(
      applyClothingCompensation(propWaist, 'waist', wornClothing),
      0.55,
      'proportional_estimate'
    );
    measurements.hipCircumference = createMeasurement(
      applyClothingCompensation(propHip, 'hip', wornClothing),
      0.55,
      'proportional_estimate'
    );
    confidences.push(0.55, 0.55, 0.55);
  }

  // ─── Proportional Estimates (Bronze tier) ────────────────────

  // Neck circumference
  const neckRatio = gender === 'female' ? 0.203 : 0.215;
  measurements.neckCircumference = createMeasurement(
    userHeightCm * neckRatio, 0.55, 'proportional_estimate'
  );
  confidences.push(0.55);

  // Upper arm circumference (Sleeve Round)
  const upperArmRatio = gender === 'female' ? 0.165 : 0.175;
  measurements.upperArmCircumference = createMeasurement(
    userHeightCm * upperArmRatio, 0.55, 'proportional_estimate'
  );
  confidences.push(0.55);

  // Wrist circumference (Round Arm)
  const wristRatio = gender === 'female' ? 0.088 : 0.092;
  measurements.wristCircumference = createMeasurement(
    userHeightCm * wristRatio, 0.55, 'proportional_estimate'
  );
  confidences.push(0.55);

  // Thigh circumference (Lap)
  const thighRatio = gender === 'female' ? 0.34 : 0.32;
  measurements.thighCircumference = createMeasurement(
    userHeightCm * thighRatio, 0.55, 'proportional_estimate'
  );
  confidences.push(0.55);

  // Knee circumference
  measurements.kneeCircumference = createMeasurement(
    userHeightCm * 0.21, 0.55, 'proportional_estimate'
  );
  confidences.push(0.55);

  // Ankle circumference (Boot)
  const ankleRatio = gender === 'female' ? 0.135 : 0.14;
  measurements.ankleCircumference = createMeasurement(
    userHeightCm * ankleRatio, 0.55, 'proportional_estimate'
  );
  confidences.push(0.55);

  // Trouser length — landmark-based (hip to ankle) if available, else proportional
  const trouserResult = measureTrouserLength(landmarks, pxPerCm, imageHeight);
  if (trouserResult) {
    measurements.trouserLength = trouserResult;
    confidences.push(trouserResult.confidence);
  } else {
    measurements.trouserLength = createMeasurement(
      userHeightCm * 0.48, 0.55, 'proportional_estimate'
    );
    confidences.push(0.55);
  }

  // Front length (Half Length: shoulder to waist, front view)
  const backLengthCm = measurements.backLength?.valueCm ?? userHeightCm * 0.26;
  measurements.frontLength = createMeasurement(
    backLengthCm * 0.65, 0.55, 'proportional_estimate'
  );
  confidences.push(0.55);

  // Sleeve length 3/4 (Short Sleeve Length)
  const armLengthCm = measurements.armLength?.valueCm ?? userHeightCm * 0.34;
  measurements.sleeveLength34 = createMeasurement(
    armLengthCm * 0.40, 0.55, 'proportional_estimate'
  );
  confidences.push(0.55);

  // Bust point (shoulder to nipple line — vertical)
  measurements.bustPoint = createMeasurement(
    backLengthCm * 0.45, 0.55, 'proportional_estimate'
  );
  confidences.push(0.55);

  // Under bust drop (shoulder to under-bust — vertical)
  measurements.underBustDrop = createMeasurement(
    backLengthCm * 0.55, 0.55, 'proportional_estimate'
  );
  confidences.push(0.55);

  // Under bust circumference
  const chestCm = measurements.chestCircumference?.valueCm ?? userHeightCm * 0.52;
  measurements.underBustCircumference = createMeasurement(
    chestCm * 0.90, 0.55, 'proportional_estimate'
  );
  confidences.push(0.55);

  // Knee length (waist to knee)
  measurements.kneeLength = createMeasurement(
    userHeightCm * 0.30, 0.55, 'proportional_estimate'
  );
  confidences.push(0.55);

  // Dress length (shoulder to mid-calf)
  measurements.dressLength = createMeasurement(
    userHeightCm * 0.58, 0.55, 'proportional_estimate'
  );
  confidences.push(0.55);

  const overallConfidence =
    confidences.length > 0
      ? confidences.reduce((a, b) => a + b, 0) / confidences.length
      : 0;

  return {
    measurements,
    overallConfidence: Math.round(overallConfidence * 100) / 100,
    measuredCount: Object.keys(measurements).length,
  };
}

// ─── Measurement Helpers ───────────────────────────────────

function measureArmLength(
  landmarks: PoseLandmarks,
  pxPerCm: number,
  imageWidth: number,
  imageHeight: number
): MeasurementValue | null {
  const leftVis = Math.min(
    landmarks[LEFT_SHOULDER]!.visibility,
    landmarks[LEFT_ELBOW]!.visibility,
    landmarks[LEFT_WRIST]!.visibility
  );
  const rightVis = Math.min(
    landmarks[RIGHT_SHOULDER]!.visibility,
    landmarks[RIGHT_ELBOW]!.visibility,
    landmarks[RIGHT_WRIST]!.visibility
  );

  const useLeft = leftVis >= rightVis;
  const bestVis = useLeft ? leftVis : rightVis;

  if (bestVis < 0.3) return null;

  const shoulder = useLeft
    ? landmarks[LEFT_SHOULDER]!
    : landmarks[RIGHT_SHOULDER]!;
  const elbow = useLeft ? landmarks[LEFT_ELBOW]! : landmarks[RIGHT_ELBOW]!;
  const wrist = useLeft ? landmarks[LEFT_WRIST]! : landmarks[RIGHT_WRIST]!;

  const upperArm = landmarkDistPx(shoulder, elbow, imageWidth, imageHeight);
  const forearm = landmarkDistPx(elbow, wrist, imageWidth, imageHeight);
  const armLengthCm = (upperArm + forearm) / pxPerCm;

  const confidence = calculateConfidence('direct_landmark', bestVis);
  return createMeasurement(armLengthCm, confidence, 'direct_landmark');
}

function measureBackLength(
  landmarks: PoseLandmarks,
  pxPerCm: number,
  imageHeight: number
): MeasurementValue | null {
  const lShoulder = landmarks[LEFT_SHOULDER]!;
  const rShoulder = landmarks[RIGHT_SHOULDER]!;
  const lHip = landmarks[LEFT_HIP]!;
  const rHip = landmarks[RIGHT_HIP]!;

  const visMean =
    (lShoulder.visibility +
      rShoulder.visibility +
      lHip.visibility +
      rHip.visibility) /
    4;
  if (visMean < 0.3) return null;

  const neckY = (lShoulder.y + rShoulder.y) / 2;
  const hipY = (lHip.y + rHip.y) / 2;

  const backLengthPx = Math.abs(hipY - neckY) * imageHeight;
  const backLengthCm = backLengthPx / pxPerCm;

  const confidence = calculateConfidence('direct_landmark', visMean);
  return createMeasurement(backLengthCm, confidence, 'direct_landmark');
}

function measureTrouserLength(
  landmarks: PoseLandmarks,
  pxPerCm: number,
  imageHeight: number
): MeasurementValue | null {
  const lHip = landmarks[LEFT_HIP]!;
  const rHip = landmarks[RIGHT_HIP]!;
  const lAnkle = landmarks[27]!; // LEFT_ANKLE
  const rAnkle = landmarks[28]!; // RIGHT_ANKLE

  const visMean = (lHip.visibility + rHip.visibility + lAnkle.visibility + rAnkle.visibility) / 4;
  if (visMean < 0.3) return null;

  const hipMidY = (lHip.y + rHip.y) / 2;
  const ankleMidY = (lAnkle.y + rAnkle.y) / 2;
  const lengthPx = Math.abs(ankleMidY - hipMidY) * imageHeight;
  const lengthCm = lengthPx / pxPerCm;

  return createMeasurement(lengthCm, calculateConfidence('direct_landmark', visMean), 'direct_landmark');
}

function measureCircumferencesFromMask(
  mask: ImageData,
  pxPerCm: number,
  depthRatios: { chest: number; waist: number; hip: number },
  chestY: number,
  waistY: number,
  hipY: number,
  bodyLeftX?: number,
  bodyRightX?: number,
  sideDepths?: { chest: number | null; waist: number | null; hip: number | null } | null
): { chest: number | null; waist: number | null; hip: number | null } {
  const result: { chest: number | null; waist: number | null; hip: number | null } = {
    chest: null,
    waist: null,
    hip: null,
  };

  const chestWidth = measureSilhouetteWidth(mask, chestY, bodyLeftX, bodyRightX);
  if (chestWidth > 0) {
    const widthCm = chestWidth / pxPerCm;
    const semiMajor = widthCm / 2;
    const semiMinor = sideDepths?.chest != null
      ? sideDepths.chest / 2
      : semiMajor * depthRatios.chest;
    result.chest = ellipseCircumference(semiMajor, semiMinor);
  }

  const waistWidth = measureSilhouetteWidth(mask, waistY, bodyLeftX, bodyRightX);
  if (waistWidth > 0) {
    const widthCm = waistWidth / pxPerCm;
    const semiMajor = widthCm / 2;
    const semiMinor = sideDepths?.waist != null
      ? sideDepths.waist / 2
      : semiMajor * depthRatios.waist;
    result.waist = ellipseCircumference(semiMajor, semiMinor);
  }

  const hipWidth = measureSilhouetteWidth(mask, hipY, bodyLeftX, bodyRightX);
  if (hipWidth > 0) {
    const widthCm = hipWidth / pxPerCm;
    const semiMajor = widthCm / 2;
    const semiMinor = sideDepths?.hip != null
      ? sideDepths.hip / 2
      : semiMajor * depthRatios.hip;
    result.hip = ellipseCircumference(semiMajor, semiMinor);
  }

  return result;
}

/**
 * Measure body depth from a side photo silhouette at chest/waist/hip Y levels.
 * Returns depth in cm for each level.
 */
function measureSideDepths(
  sideMask: ImageData,
  pxPerCm: number,
  chestY: number,
  waistY: number,
  hipY: number
): { chest: number | null; waist: number | null; hip: number | null } {
  const chestWidth = measureSilhouetteWidth(sideMask, chestY);
  const waistWidth = measureSilhouetteWidth(sideMask, waistY);
  const hipWidth = measureSilhouetteWidth(sideMask, hipY);

  return {
    chest: chestWidth > 0 ? chestWidth / pxPerCm : null,
    waist: waistWidth > 0 ? waistWidth / pxPerCm : null,
    hip: hipWidth > 0 ? hipWidth / pxPerCm : null,
  };
}

// ─── Utility Functions (exported for testing) ──────────────

/**
 * Calculate Euclidean distance between two landmarks in pixels.
 * Landmarks have normalized [0,1] coordinates that are scaled by image dimensions.
 */
export function landmarkDistPx(
  a: { x: number; y: number },
  b: { x: number; y: number },
  imageWidth: number,
  imageHeight: number
): number {
  const dx = (a.x - b.x) * imageWidth;
  const dy = (a.y - b.y) * imageHeight;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Approximate the circumference of an ellipse using Ramanujan's formula.
 * More accurate than pi*(a+b) for non-circular cross-sections.
 *
 * @param a - Semi-major axis (half of front width in cm)
 * @param b - Semi-minor axis (estimated depth in cm)
 */
export function ellipseCircumference(a: number, b: number): number {
  return Math.PI * (3 * (a + b) - Math.sqrt((3 * a + b) * (a + 3 * b)));
}

/**
 * Measure the horizontal width of the body silhouette at a given normalized Y position.
 * Scans the segmentation mask row to find leftmost and rightmost body pixels.
 *
 * When bodyLeftX/bodyRightX are provided, the scan is limited to the torso region
 * (between inner shoulder/hip edges ± margin), excluding arm pixels that would
 * inflate the measurement.
 *
 * @param mask - Binary segmentation mask (R channel > 128 = body)
 * @param normalizedY - Y position as fraction of image height [0, 1]
 * @param bodyLeftX - Optional normalized X of left body edge (shoulder/hip)
 * @param bodyRightX - Optional normalized X of right body edge (shoulder/hip)
 * @returns Width in pixels, or 0 if no body found at this Y
 */
export function measureSilhouetteWidth(
  mask: ImageData,
  normalizedY: number,
  bodyLeftX?: number,
  bodyRightX?: number
): number {
  const row = Math.round(normalizedY * (mask.height - 1));
  if (row < 0 || row >= mask.height) return 0;

  // Determine scan range — if body edges provided, add 10% margin to exclude arms
  let scanLeft = 0;
  let scanRight = mask.width - 1;
  if (bodyLeftX != null && bodyRightX != null) {
    const margin = (bodyRightX - bodyLeftX) * 0.10;
    scanLeft = Math.max(0, Math.round((bodyLeftX - margin) * mask.width));
    scanRight = Math.min(mask.width - 1, Math.round((bodyRightX + margin) * mask.width));
  }

  let leftmost = -1;
  let rightmost = -1;

  for (let x = scanLeft; x <= scanRight; x++) {
    const idx = (row * mask.width + x) * 4;
    if (mask.data[idx]! > 128) {
      if (leftmost === -1) leftmost = x;
      rightmost = x;
    }
  }

  if (leftmost === -1) return 0;
  return rightmost - leftmost + 1;
}

/**
 * Apply clothing compensation to a circumference measurement.
 * Subtracts the estimated fabric thickness based on clothing type.
 */
export function applyClothingCompensation(
  valueCm: number,
  bodyPart: 'chest' | 'waist' | 'hip' | 'thigh',
  clothingType: WornClothingType
): number {
  const compensation = CLOTHING_COMPENSATION[clothingType][bodyPart];
  return Math.max(0, valueCm - compensation);
}

/**
 * Calculate measurement confidence based on method type and landmark visibility.
 * Higher visibility landmarks produce higher confidence scores.
 */
export function calculateConfidence(
  method: MeasurementMethod,
  visibility: number
): number {
  const baseConfidence: Record<MeasurementMethod, number> = {
    direct_landmark: 0.9,
    multi_view_circumference: 0.92,
    silhouette_enhanced: 0.82,
    computed_circumference: 0.8,
    proportional_estimate: 0.55,
  };

  const base = baseConfidence[method];
  // Map visibility from [0.3, 1.0] to multiplier [0.7, 1.0]
  const clampedVis = Math.min(1, Math.max(0.3, visibility));
  const visMultiplier = 0.7 + ((clampedVis - 0.3) / 0.7) * 0.3;
  return Math.round(base * visMultiplier * 100) / 100;
}

/**
 * Map a numeric confidence score to a human-readable level.
 */
export function getConfidenceLevel(confidence: number): ConfidenceLevel {
  if (confidence >= 0.8) return 'high';
  if (confidence >= 0.6) return 'medium';
  return 'low';
}

/**
 * Compute confidence for multi-view circumference based on observation count.
 * More views → higher confidence (4: 0.82, 8: 0.90, 12: 0.94).
 */
export function multiViewConfidence(observationCount: number): number {
  if (observationCount <= 2) return 0.75;
  if (observationCount <= 4) return 0.82;
  if (observationCount <= 6) return 0.86;
  if (observationCount <= 8) return 0.90;
  if (observationCount <= 10) return 0.92;
  return 0.94;
}

// ─── Internal Helpers ──────────────────────────────────────

function createMeasurement(
  valueCm: number,
  confidence: number,
  method: MeasurementMethod
): MeasurementValue {
  return {
    valueCm: Math.round(valueCm * 10) / 10,
    confidence,
    confidenceLevel: getConfidenceLevel(confidence),
    method,
  };
}
